import { ReactElement, useEffect, useState, useMemo } from 'react';
import dynamic from 'next/dynamic';
import { NextPage } from 'next';
import { AppProps } from 'next/app';
import { useRouter } from 'next/router';
import { Provider } from 'react-redux';
import { CssBaseline, StyledEngineProvider, ThemeProvider } from '@mui/material';
import { PersistGate } from 'redux-persist/integration/react';
import { persistStore } from 'redux-persist';
import '../src/index.css';
import '../src/reset.css';
import 'slick-carousel/slick/slick.css';
import 'slick-carousel/slick/slick-theme.css';
import { useStore, useTypedSelector } from '../src/store/store';
import { InitialContainer } from '../src/common/components/InitialContainer/InitialContainer';
import { theme } from '../src/theme';
import { init } from '../utils/sentry';
import { ToastComponent } from '../src/common/components/Toast/ToastComponent';
import { getAlteredTheme } from '../src/services/hooks/themeHook';
import App, { AppContext } from 'next/app';
// Clarity service will be imported dynamically

import { isMobile } from 'react-device-detect';
import { GA_MEASUREMENT_ID } from '../src/common/constants/constants';
import { whenUploadQuiet } from '../src/services/upload/uploadQuiet';



if (process.env.APP_ENV !== 'development') {
  init();
}

// Microsoft Clarity will be initialized in useEffect

type NextPageWithLayout = NextPage & {
  getLayout?: (page: ReactElement) => ReactElement;
};

type AppPropsWithLayout = AppProps & {
  Component: NextPageWithLayout;
};

// Removed custom generateClassName to prevent DOM insertion errors

// Inner component that has access to Redux store
function AppContent({ Component, pageProps, getLayout }: { Component: NextPageWithLayout; pageProps: any; getLayout: (page: ReactElement) => ReactElement }) {
  const { accountAttributes } = useTypedSelector((state) => state.account);
  
  // Extract color values for memoization (to prevent theme re-creation when object reference changes)
  const primaryColor = accountAttributes?.properties?.['webapp.config']?.['primary-color'];
  const secondaryColor = accountAttributes?.properties?.['webapp.config']?.['secondary-color'];
  const textColor = accountAttributes?.properties?.['webapp.config']?.['text-color'];
  const hasProperties = !!accountAttributes?.properties;
  
  // Memoize theme based on actual color values, not object reference
  const appTheme = useMemo(() => {
    if (!hasProperties) {
      console.log('🎨 Using default theme (no properties)');
      return theme;
    }
    console.log('🎨 Creating custom theme:', { primaryColor, secondaryColor, textColor });
    return getAlteredTheme(accountAttributes);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [primaryColor, secondaryColor, textColor, hasProperties]);

  // Log when AppContent renders with theme
  useEffect(() => {
    const emotionStyles = document.querySelectorAll('style[data-emotion]');
    console.log('🎨 Emotion styles injected:', emotionStyles.length);
  }, []);

  return (
    <StyledEngineProvider injectFirst>
      <ThemeProvider theme={appTheme}>
        <CssBaseline />
        <InitialContainer>{getLayout(<Component {...pageProps} />)}</InitialContainer>
      </ThemeProvider>
    </StyledEngineProvider>
  );
}

function MyApp({ Component, pageProps }: AppPropsWithLayout) {
  const router = useRouter();
  const [, setLoading] = useState(true);

  // Debug CSS class generation
  useEffect(() => {
    // const debugInfo = {
    //   isServer: typeof window === 'undefined',
    //   windowDefined: typeof window !== 'undefined',
    //   pathname: router.pathname,
    //   timestamp: new Date().toISOString()
    // };
    // console.log('App render context:', debugInfo);
    
    // if (typeof window !== 'undefined') {
    //   const jssStyles = document.head.querySelector('style[data-jss]');
    //   const emotionStyles = document.head.querySelectorAll('style[data-emotion]');
    //   console.log('🎨 CSS Debug:', {
    //     jssStylesLength: jssStyles?.textContent?.length || 0,
    //     emotionStylesCount: emotionStyles.length,
    //     jssPreview: jssStyles?.textContent?.slice(0, 200) + '...',
    //     totalStyleTags: document.head.querySelectorAll('style').length
    //   });
    // }
  }, [router.pathname]);

  useEffect(() => {
    const handleStart = (url: string) => setLoading(url !== router.pathname);
    const handleComplete = () => setLoading(false);

    router.events.on('routeChangeStart', handleStart);
    router.events.on('routeChangeComplete', handleComplete);
    router.events.on('routeChangeError', handleComplete);
  }, [router]);

  // Microsoft Clarity initialization and tracking
  useEffect(() => {
    let clarityServiceRef: any = null;
    let lastTrackedUrl = '';
    
    const initializeClarity = async () => {
      // console.log('🔍 Clarity Debug:', {
      //   isClient: typeof window !== 'undefined',
      //   hasProjectId: !!process.env.NEXT_PUBLIC_CLARITY_PROJECT_ID,
      //   projectId: process.env.NEXT_PUBLIC_CLARITY_PROJECT_ID ? 'SET' : 'MISSING',
      //   hasRef: !!clarityServiceRef
      // });
      
      if (typeof window !== 'undefined' && process.env.NEXT_PUBLIC_CLARITY_PROJECT_ID && !clarityServiceRef) {
        try {
          // An upload between file chosen and first part confirmed holds the
          // recorder back (capped at 30 s); otherwise this resolves at once.
          await whenUploadQuiet();
          // Dynamic import to avoid SSR issues
          const { clarityService } = await import('../src/services/clarity/clarityService');
          clarityServiceRef = clarityService;
          
          // Only initialize if not already initialized
          if (!clarityService.initialized) {
            clarityService.init(process.env.NEXT_PUBLIC_CLARITY_PROJECT_ID, GA_MEASUREMENT_ID);
            // console.log('✅ Clarity initialized successfully!');
            
            // Upgrade all sessions since they're short and focused
            clarityService.upgrade('session-started-short-focused-interaction');
            // console.log('🚀 Clarity session upgraded for focused interaction');
          }
          
          // Track initial page load
          lastTrackedUrl = router.asPath;
          clarityService.trackPageNavigation(router.asPath);
          // console.log('📍 Clarity page tracked:', router.asPath);
        } catch (error) {
          console.error('Failed to initialize Clarity:', error);
        }
      }
    };

    const handleRouteChange = (url: string) => {
      // Prevent duplicate tracking of the same URL
      if (clarityServiceRef && clarityServiceRef.initialized && url !== lastTrackedUrl) {
        lastTrackedUrl = url;
        clarityServiceRef.trackPageNavigation(url);
      }
    };

    // Defer to idle (Aug 2026 tag audit): Clarity's recorder was initializing
    // at mount, competing with app boot and — on the upload path — with
    // chunk 1 for a thin cellular uplink. Session replay loses nothing by
    // starting a few seconds late; the 8s timeout guarantees it still starts
    // on browsers without requestIdleCallback or pages that never go idle.
    let idleHandle: number | null = null;
    let idleTimer: ReturnType<typeof setTimeout> | null = null;
    if(process.env.APP_ENV !== 'development'){
      if (typeof window !== 'undefined' && 'requestIdleCallback' in window) {
        idleHandle = (window as any).requestIdleCallback(() => initializeClarity(), { timeout: 8000 });
      } else {
        idleTimer = setTimeout(() => initializeClarity(), 5000);
      }
    }
    

    // Listen for route changes
    router.events.on('routeChangeComplete', handleRouteChange);
    
    return () => {
      router.events.off('routeChangeComplete', handleRouteChange);
      if (idleHandle !== null && typeof window !== 'undefined' && 'cancelIdleCallback' in window) {
        (window as any).cancelIdleCallback(idleHandle);
      }
      if (idleTimer !== null) clearTimeout(idleTimer);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Empty dependency array - run only once

  const isWindow = typeof window !== 'undefined';

  useEffect(() => {
    if ('serviceWorker' in navigator) {
      navigator.serviceWorker
        .register('/service-worker.js')
        .then((registration) => console.log('scope is: ', registration.scope));
    }
  }, []);

  useEffect(() => {
    let vh = 768;
    if (isWindow) {
      vh = window.innerHeight * 0.01;
    }
    // Then we set the value in the --vh custom property to the root of the document
    document.documentElement.style.setProperty('--vh', `${vh}px`);
    if (!isMobile) {
      document.body.style.setProperty('overflow', 'hidden');
    }

    // No more JSS cleanup needed - pure Emotion now! 🎉

    const resize = () => {
      const doc = document.documentElement;

      const prevAppHeight = doc.style.getPropertyValue('--app-height').replace('px', '');

      if (!prevAppHeight || !isMobile || Math.abs(Number(prevAppHeight) - window.innerHeight) < 100) {
        doc.style.setProperty('--app-height', `${window.innerHeight}px`);
      }
    };
    window.addEventListener('resize', resize);
    resize();
    
    return () => {
      window.removeEventListener('resize', resize);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const store = useStore((pageProps as any).initialReduxState);
  const persistor = persistStore(store);
  const getLayout = Component.getLayout ?? ((page) => <>{page}</>);

  // Client-side only rendering - wait for Redux to rehydrate
  if (typeof window !== 'undefined' && isWindow) {
    const CrispWithNoSSR = dynamic(() => import('../src/services/crisp/CrispChat'), { ssr: false });
    return (
      <div suppressHydrationWarning>
        <Provider store={store}>
          <PersistGate 
            loading={<div />} 
            persistor={persistor}
            onBeforeLift={() => {
              console.log('✅ Redux rehydrated - ready to render');
            }}
          >
            <AppContent Component={Component} pageProps={pageProps} getLayout={getLayout} />
            <ToastComponent />
            {<CrispWithNoSSR />}
          </PersistGate>
        </Provider>
      </div>
    );
  }

  // SSR returns null - we'll handle everything client-side.
  // Meta tags are injected via _document.tsx so social crawlers still get OG/Twitter metadata.
  return null;
}

const BLOCKED_IPS = ['185.177.72.144', '185.177.72.179'];

MyApp.getInitialProps = async (context: AppContext) => {
  const { req, res } = context.ctx;

  if (req && res) {
    const forwarded = req.headers['x-forwarded-for'];
    const ip = typeof forwarded === 'string' ? forwarded.split(/, /)[0] : req.socket.remoteAddress;

    if (ip && BLOCKED_IPS.includes(ip)) {
      res.writeHead(403);
      res.end('Forbidden');
      return {};
    }
  }

  const appProps = await App.getInitialProps(context);
  return { ...appProps };
};

export default MyApp;
